import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AlertDialog from 'components/UI/AlertDialog';
import moment from 'moment';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { CampaignData } from 'services/api/adminCampaigns.api';
import { useUploadWhatsAppCampaignClientsMutation } from 'services/api/adminCampaigns.api';
import baseApi from 'services/api/baseApi';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

import MessageSchedulePreview from './MessageSchedulePreview';

export interface CampaignUploadResult {
  createdClients: number[];
  updatedClients: number[];
  failedClients: { phone: string; error: string }[];
  skippedClients: { row: number; phone: string; reason: string }[];
  parseErrors: { row: number; code: string; message: string }[];
  createdCars: number;
  updatedCars: number;
  skippedCars: { row: number; identifier: string; reason: string }[];
  failedCars: { row: number; identifier: string; error: string }[];
  aborted: boolean;
}

interface CampaignConfigurationProps {
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSuccess: (result: CampaignUploadResult) => void;
  onBack: () => void;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE_CONTAINER_LOWEST,
    borderRadius: RADIUS_ITEM,
    fontFamily: 'Jost, sans-serif',
    '& fieldset': { border: 'none' },
    boxShadow: SHADOW,
  },
  '& label': { fontFamily: 'Jost, sans-serif' },
  '& .MuiFormHelperText-root': { fontFamily: 'Jost, sans-serif' },
};

const CampaignConfiguration: React.FC<CampaignConfigurationProps> = ({
  campaignData,
  setCampaignData,
  onSuccess,
  onBack,
}) => {
  const [rawInputs, setRawInputs] = useState({
    messagesToSendPerPeriod: String(campaignData.messagesToSendPerPeriod),
    timePeriodInMinutes: String(campaignData.timePeriodInMinutes),
    startHour: campaignData.startHour != null ? String(campaignData.startHour) : '',
    endHour: campaignData.endHour != null ? String(campaignData.endHour) : '',
  });

  const handleCampaignNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCampaignData((prev) => ({ ...prev, campaignName: e.target.value }));
  };

  const handleMessagesPerPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setRawInputs((prev) => ({ ...prev, messagesToSendPerPeriod: raw }));
    const value = parseInt(raw, 10);
    if (!Number.isNaN(value) && value > 0) {
      setCampaignData((prev) => ({ ...prev, messagesToSendPerPeriod: value }));
    }
  };

  const handleTimePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setRawInputs((prev) => ({ ...prev, timePeriodInMinutes: raw }));
    const value = parseInt(raw, 10);
    if (!Number.isNaN(value) && value > 0) {
      setCampaignData((prev) => ({ ...prev, timePeriodInMinutes: value }));
    }
  };

  const handleStartHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setRawInputs((prev) => ({ ...prev, startHour: raw }));
    if (raw === '') {
      setCampaignData((prev) => ({ ...prev, startHour: null }));
      return;
    }
    const value = parseInt(raw, 10);
    if (!Number.isNaN(value) && value >= 0 && value <= 23) {
      setCampaignData((prev) => ({ ...prev, startHour: value }));
    }
  };

  const handleEndHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setRawInputs((prev) => ({ ...prev, endHour: raw }));
    if (raw === '') {
      setCampaignData((prev) => ({ ...prev, endHour: null }));
      return;
    }
    const value = parseInt(raw, 10);
    if (!Number.isNaN(value) && value >= 0 && value <= 23) {
      setCampaignData((prev) => ({ ...prev, endHour: value }));
    }
  };

  const handleSendOnWeekendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCampaignData((prev) => ({ ...prev, sendOnWeekend: e.target.checked }));
  };

  const handleStartDateChange = (newValue: moment.Moment | null) => {
    setCampaignData((prev) => ({ ...prev, startDate: newValue?.toDate() || null }));
  };

  const [uploadCampaign, { isError, error, isLoading }] = useUploadWhatsAppCampaignClientsMutation();
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isError) setShowError(true);
  }, [isError]);

  const handleCreateCampaign = async () => {
    if (
      !campaignData.uploadedFile ||
      !campaignData.mappedHeaders ||
      !campaignData.businessUnitId ||
      !campaignData.selectedTemplate
    ) {
      return;
    }

    try {
      const result = await uploadCampaign({
        businessUnitId: campaignData.businessUnitId,
        campaignName: campaignData.campaignName,
        templateName: campaignData.selectedTemplate.name,
        mappedHeaders: campaignData.mappedHeaders,
        separator: campaignData.separator || ';',
        messagesToSendPerPeriod: campaignData.messagesToSendPerPeriod,
        timePeriodInMinutes: campaignData.timePeriodInMinutes,
        startDate: campaignData.startDate || undefined,
        startHour: campaignData.startHour || undefined,
        endHour: campaignData.endHour || undefined,
        sendOnWeekend: campaignData.sendOnWeekend,
        templateComponents: {
          bodyComponents: campaignData.templateBodyParameters,
        },
        file: campaignData.uploadedFile,
      }).unwrap();
      // Cross-API invalidation: campaign upload lives on baseWorkloadApi but Leads / Clients
      // queries live on baseApi, so RTK's automatic tag invalidation does not reach them.
      // Drop the cached lists so the next visit to the Leads / Clients view refetches.
      dispatch(
        baseApi.util.invalidateTags([
          { type: 'Leads', id: `BU-${campaignData.businessUnitId}` },
          { type: 'Leads', id: 'BU-ALL' },
          'Client',
        ])
      );

      onSuccess({
        createdClients: result.createdClients,
        updatedClients: result.updatedClients,
        failedClients: result.failedClients,
        skippedClients: result.skippedClients,
        parseErrors: result.parseErrors ?? [],
        createdCars: result.createdCars ?? 0,
        updatedCars: result.updatedCars ?? 0,
        skippedCars: result.skippedCars ?? [],
        failedCars: result.failedCars ?? [],
        aborted: result.aborted,
      });
    } catch (e) {
      console.error(e);
      setShowError(true);
    }
  };

  const canProceed = campaignData.campaignName.trim() !== '';

  const isInvalidSchedule =
    campaignData.startHour !== null &&
    campaignData.startHour !== undefined &&
    campaignData.endHour !== null &&
    campaignData.endHour !== undefined &&
    campaignData.startHour >= campaignData.endHour;

  const sectionTitle = (text: string) => (
    <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 0.5 }}>
      {text}
    </Typography>
  );

  const sectionSubtitle = (text: string) => (
    <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.55, mb: 2.5 }}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 700, mx: 'auto' }}>
      <AlertDialog
        openDialog={showError}
        setOpenDialog={setShowError}
        header="Error al crear campaña"
        msg={JSON.stringify(error)}
        cancelMsg="Cerrar"
      />

      {/* Campaign name */}
      <Box>
        {sectionTitle('Nombre de la Campaña')}
        {sectionSubtitle('Un identificador único para esta campaña.')}
        <TextField
          label="Nombre de la Campaña"
          value={campaignData.campaignName}
          onChange={handleCampaignNameChange}
          required
          fullWidth
          sx={fieldSx}
        />
      </Box>

      {/* Start date */}
      <Box>
        {sectionTitle('Fecha y Hora de Inicio')}
        {sectionSubtitle('Define cuándo comenzará a ejecutarse la campaña.')}
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateTimePicker
            label="Fecha y hora de inicio"
            value={campaignData.startDate ? moment(campaignData.startDate) : moment().add(1, 'hour')}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY HH:mm"
            minDateTime={moment()}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: fieldSx,
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Send limits */}
      <Box>
        {sectionTitle('Límites de Envío')}
        {sectionSubtitle('Controla la velocidad de envío para evitar sobrecarga.')}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <TextField
            label="Mensajes por Período"
            type="number"
            value={rawInputs.messagesToSendPerPeriod}
            onChange={handleMessagesPerPeriodChange}
            fullWidth
            sx={fieldSx}
            slotProps={{ htmlInput: { min: 1 } }}
          />
          <TextField
            label="Período (minutos)"
            type="number"
            value={rawInputs.timePeriodInMinutes}
            onChange={handleTimePeriodChange}
            fullWidth
            sx={fieldSx}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.5 }}>
          Se enviarán {campaignData.messagesToSendPerPeriod} mensajes cada {campaignData.timePeriodInMinutes} minutos
        </Typography>
      </Box>

      {/* Schedule */}
      <Box>
        {sectionTitle('Horario de Operación')}
        {sectionSubtitle('Define en qué franja horaria se enviarán los mensajes. Deja vacío para sin restricción.')}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Hora de Inicio"
            type="number"
            value={rawInputs.startHour}
            onChange={handleStartHourChange}
            helperText="0–23 (formato 24h)"
            fullWidth
            sx={fieldSx}
            slotProps={{ htmlInput: { min: 0, max: 23 } }}
          />
          <TextField
            label="Hora de Fin"
            type="number"
            value={rawInputs.endHour}
            onChange={handleEndHourChange}
            helperText="0–23 (formato 24h)"
            fullWidth
            sx={fieldSx}
            slotProps={{ htmlInput: { min: 0, max: 23 } }}
          />
        </Box>

        {!isInvalidSchedule && campaignData.startHour != null && campaignData.endHour != null && (
          <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.55, mb: 1.5 }}>
            Mensajes entre las {campaignData.startHour}:00 y las {campaignData.endHour}:00
          </Typography>
        )}

        {isInvalidSchedule && (
          <Box
            sx={{
              bgcolor: 'rgba(176, 0, 32, 0.07)',
              borderRadius: RADIUS_ITEM,
              p: 1.5,
              mb: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: '#B00020', fontWeight: 600 }}>
              Horario inválido — la hora de inicio ({campaignData.startHour}:00) debe ser menor que la de fin (
              {campaignData.endHour}:00).
            </Typography>
          </Box>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={campaignData.sendOnWeekend}
              onChange={handleSendOnWeekendChange}
              sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY }}>
              Enviar mensajes durante el fin de semana
            </Typography>
          }
        />
      </Box>

      {/* Schedule preview */}
      {!isInvalidSchedule && campaignData.totalFileRows && (
        <Box>
          {sectionTitle('Vista Previa del Cronograma')}
          {sectionSubtitle('Estimado basado en la cantidad de filas del archivo subido.')}
          <MessageSchedulePreview
            messagesToSendPerPeriod={campaignData.messagesToSendPerPeriod}
            totalClients={campaignData.totalFileRows}
            timePeriodInMinutes={campaignData.timePeriodInMinutes}
            startDate={campaignData.startDate}
            startHour={campaignData.startHour}
            endHour={campaignData.endHour}
            sendOnWeekend={campaignData.sendOnWeekend}
          />
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          onClick={onBack}
          disabled={isLoading}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            color: NAVY,
            bgcolor: 'rgba(21, 24, 58, 0.07)',
            borderRadius: RADIUS_COIN,
            px: 2.5,
            '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
          }}
        >
          Atrás
        </Button>
        <Button
          onClick={handleCreateCampaign}
          disabled={!canProceed || isLoading || isInvalidSchedule}
          startIcon={isLoading ? <CircularProgress size={16} sx={{ color: NAVY }} /> : undefined}
          sx={{
            bgcolor: YELLOW,
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            px: 4,
            boxShadow: SHADOW,
            '&:hover': { bgcolor: '#e6c009' },
            '&:disabled': { bgcolor: 'rgba(21,24,58,0.1)', color: 'rgba(21,24,58,0.3)' },
          }}
        >
          {isLoading ? 'Creando...' : 'Crear Campaña'}
        </Button>
      </Box>
    </Box>
  );
};

export default CampaignConfiguration;
